import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../AppIcon';
import {
  MapPin,
  Phone,
  Mail,
  ArrowRight,
} from 'lucide-react';

const Footer = forwardRef(({ variant = 'full' }, ref) => {
  const currentYear = new Date().getFullYear();

  const navigationLinks = {
    quickLinks: [
      { label: 'Home', href: '/' },
      { label: 'About Us', href: '/about-us' },
      { label: 'Services', href: '/services' },
      { label: 'Technologies', href: '/technologies' },
      { label: 'Contact Us', href: '/contact-us' },
    ],
    company: [
      { label: 'About Us', href: '/about-us' },
      { label: 'Careers', href: '/careers' },
      { label: 'Blog', href: '/blog' },
      { label: 'Press', href: '/press' },
    ],
    services: [
      { label: 'AI Automation', href: '/services' },
      { label: 'Custom Internal Tools', href: '/services' },
      { label: 'Web Application Development', href: '/services' },
      { label: 'Cloud Integration Services', href: '/services' },
    ],
    technologies: [
      { label: 'Frontend', href: '/technologies' },
      { label: 'Backend', href: '/technologies' },
      { label: 'Mobile', href: '/technologies' },
      { label: 'Cloud', href: '/technologies' },
    ],
  };

  const socialLinks = [
    { label: 'Twitter', icon: 'Twitter', href: 'https://twitter.com' },
    { label: 'LinkedIn', icon: 'Linkedin', href: 'https://www.linkedin.com/company/zovox-technologies' },
    { label: 'GitHub', icon: 'Github', href: 'https://github.com' },
    { label: 'Facebook', icon: 'Facebook', href: 'https://facebook.com' },
  ];

  // Simple Footer
  if (variant === 'simple') {
    return (
      <footer
        ref={ref}
        className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white overflow-hidden"
        role="contentinfo"
      >
        {/* CHANGED: Removed padding from container, added to inner div */}
        <div className="container mx-auto 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
          <div className="px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <motion.div
                className="flex items-center mb-6 md:mb-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Link to="/" className="flex items-center group">
                  <div className="relative">
                    <svg
                      className="h-10 w-auto text-blue-500 group-hover:text-blue-400 transition-colors duration-300"
                      viewBox="0 0 40 40"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 5L30 10V30L20 35L10 30V10L20 5Z"
                        fill="currentColor"
                        fillOpacity="0.2"
                      />
                      <path
                        d="M20 5L30 10V30L20 35L10 30V10L20 5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 15L25 17.5V25L20 27.5L15 25V17.5L20 15Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <span className="ml-3 text-2xl font-bold text-white">ZovoX</span>
                </Link>
              </motion.div>

              <motion.div
                className="flex gap-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {socialLinks.map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-blue-600 transition-all duration-300 border border-white/10"
                    aria-label={link.label}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon name={link.icon} size={18} />
                  </motion.a>
                ))}
              </motion.div>
            </div>

            <div className="mt-10 border-t border-slate-700 pt-8">
              <p className="text-sm text-slate-400 text-center">
                &copy; {currentYear} ZovoX Technologies. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full Footer
  return (
    <footer
      ref={ref}
      className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700"
      role="contentinfo"
    >
      {/* CHANGED: Removed padding from container, added to inner div */}
      <div className="container mx-auto relative z-10 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
        <div className="px-3 py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12 3xl:gap-20">
            {/* Company Info */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center gap-2 group">
                  <img
                    src="/assets/images/header.png"
                    alt="ZovoX logo"
                    className="h-16 3xl:h-20 w-auto group-hover:scale-105 transition-transform duration-300 rounded-md"
                  />
                </Link>
              </div>

              <p className="mt-6 text-base 3xl:text-xl text-slate-300 leading-relaxed max-w-sm 3xl:max-w-md">
                ZovoX Technologies – Innovative digital solutions to help your business grow smarter and faster.
              </p>

              <div className="mt-8 3xl:mt-10">
                <h3 className="text-sm 3xl:text-base font-bold text-white tracking-wider uppercase flex items-center gap-2 mb-4 3xl:mb-6">
                  <Phone className="w-5 h-5 3xl:w-6 3xl:h-6 text-blue-400" />
                  Contact Us
                </h3>
                <div className="space-y-3 3xl:space-y-4">
                  <motion.div
                    className="flex items-start text-slate-300 group cursor-pointer"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <MapPin className="w-5 h-5 3xl:w-6 3xl:h-6 mr-3 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="group-hover:text-white transition-colors 3xl:text-lg">Chandigarh, India</span>
                  </motion.div>

                  <motion.div
                    className="flex items-center text-slate-300 group"
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Mail className="w-5 h-5 3xl:w-6 3xl:h-6 mr-3 text-blue-400 flex-shrink-0" />
                    <a href="mailto:contact@zovox.in" className="group-hover:text-white transition-colors 3xl:text-lg">
                      contact@zovox.in
                    </a>
                  </motion.div>
                </div>
              </div>

              <div className="mt-8 3xl:mt-10">
                <h3 className="text-sm 3xl:text-base font-bold text-white tracking-wider uppercase mb-4 3xl:mb-6">
                  Follow Us
                </h3>
                <div className="flex gap-3 3xl:gap-4">
                  {socialLinks.map((link, index) => (
                    <motion.a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 3xl:w-14 3xl:h-14 rounded-xl bg-white/5 backdrop-blur-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 transition-all duration-300 border border-white/10 shadow-lg"
                      aria-label={link.label}
                      whileHover={{ scale: 1.1, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Icon name={link.icon} size={20} className="3xl:w-7 3xl:h-7" />
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              className="lg:col-span-1 lg:pl-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-bold text-white tracking-wider uppercase mb-6 3xl:mb-8 flex items-center gap-2">
                <div className="w-1 h-5 3xl:h-6 bg-cyan-500 rounded-full" />
                Quick Links
              </h3>
              <ul className="space-y-3 3xl:space-y-4" role="list" aria-label="Quick links">
                {navigationLinks.quickLinks.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      className="text-base 3xl:text-lg text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 3xl:w-5 3xl:h-5 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-cyan-400" />
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              className="lg:col-span-1 lg:pl-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-sm 3xl:text-base font-bold text-white tracking-wider uppercase mb-6 3xl:mb-8 flex items-center gap-2">
                <div className="w-1 h-5 3xl:h-6 bg-blue-500 rounded-full" />
                Services
              </h3>
              <ul className="space-y-3 3xl:space-y-4" role="list" aria-label="Services links">
                {navigationLinks.services.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      className="text-base 3xl:text-lg text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 3xl:w-5 3xl:h-5 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-blue-400" />
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Technologies */}
            <motion.div
              className="lg:col-span-1 lg:pl-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-sm 3xl:text-base font-bold text-white tracking-wider uppercase mb-6 3xl:mb-8 flex items-center gap-2">
                <div className="w-1 h-5 3xl:h-6 bg-purple-500 rounded-full" />
                Technologies
              </h3>
              <ul className="space-y-3 3xl:space-y-4" role="list" aria-label="Technologies links">
                {navigationLinks.technologies.map((link, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      className="text-base 3xl:text-lg text-slate-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 3xl:w-5 3xl:h-5 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-purple-400" />
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Copyright */}
          <motion.div
            className="mt-16 border-t border-slate-700 pt-8 "
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <p className="text-sm 3xl:text-base text-slate-400 text-center">
              &copy; {currentYear} ZovoX Technologies. All rights reserved.
            </p>
          </motion.div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
